<template>
  <section id="appointment-questionnaire" class="px-3">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">
    </Breadcrumbs>

    <div v-if="isLoading" class="d-flex justify-content-center align-items-center flex-column py-4">
      <span class="mb-3">{{ $t('clinical.people.anamnesis.loading') }}</span>
      <hollow-dots-spinner
        :animation-duration="1000"
        :size="60"
        color="#29c4a9"
      />
    </div>

    <div v-else-if="appointment && person" class="my-5">

      <section id="person-data" class="questionnaire-section">

        <h6 class="is-bold mb-3">
          {{ $t('clinical.people.anamnesis.person_data') }}
        </h6>

        <PersonForm
          key="person-form"
          v-if="person"
          v-model="personForm.data"
          :service="component.service"
          :editing="true"
          :selectedPerson="person"
          :formErrors="personForm.errors"
        />

      </section>

      <section id="appointment-data" class="questionnaire-section">

        <AppointmentScheduleAndCompanion
          ref="scheduleAndCompanionForm"
          :appointment="appointment"
          v-model="appointmentForm.data"
          :editing="true"
          :service="component.service"
          :formErrors="appointmentForm.appointmentErrors.rootErrors()"
          @clearErrors="field => appointmentForm.appointmentErrors.clear(field)"
        />

        <va-button
          id="btn-open-companion-file"
          v-show="appointmentForm.data.has_companion_person && appointmentForm.data.companion_person && appointmentForm.data.companion_person.id === appointment.companion_person.id"
          @click="handleGoToCompanionFileClicked"
          :disabled=" isSubmitingPersonForm || isSubmitingAppointmentForm || isEndingAppointment">
          {{ $t('clinical.people.open_file') }}
        </va-button>

      </section>

      <section class="questionnaire-section">
        <h6 class="is-bold">
          {{ $t('clinical.people.anamnesis.clinical_questionnaire') }}
        </h6>

        <ClinicalQuestionnaireForm
          ref="clinicalQuestionnaireForm"
          class="ml-3"
          v-model="appointmentForm.data.clinical_questionnaire"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('clinical_questionnaire.')"
          @clearErrors="field => appointmentForm.anamnesisErrors.clear(`clinical_questionnaire.${field}`)"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-5">
            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.clinical_questionnaire)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>
          </div>

        </ClinicalQuestionnaireForm>

        <LifeStyleForm
          ref="lifeStyleForm"
          class="ml-3"
          v-model="appointmentForm.data.life_style"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('life_style.')"
          @clearErrors="field => appointmentForm.anamnesisErrors.clear(`life_style.${field}`)"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-5">
            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.life_style)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>
          </div>

        </LifeStyleForm>

        <OtoscopyForm
          ref="otoscopyForm"
          class="ml-3"
          v-model="appointmentForm.data.otoscopy"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('otoscopy.')"
          @clearErrors="field => {appointmentForm.anamnesisErrors.clear(`otoscopy.${field}`); appointmentForm.appointmentErrors.clear('otoscopy')}"
          :collapsable="true"
          :editing="appointmentForm.data.with_otoscopy !== null && appointmentForm.data.with_otoscopy"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.otoscopy)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>

            <!-- With Otoscopy -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{ $t('clinical.people.anamnesis.with_otoscopy') }}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="appointmentForm.data.with_otoscopy"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="appointmentForm.appointmentErrors.clear('with_otoscopy'); appointmentForm.appointmentErrors.clear('otoscopy')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('with_otoscopy')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('with_otoscopy')) }}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('otoscopy')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('otoscopy')) }}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>
        </OtoscopyForm>

        <ImpedanceForm
          ref="impedanceForm"
          class="ml-3"
          v-model="appointmentForm.data.impedance"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('impedance.')"
          @clearErrors="field => {appointmentForm.anamnesisErrors.clear(`impedance.${field}`); appointmentForm.appointmentErrors.clear('impedance')}"
          :collapsable="true"
          :editing="appointmentForm.data.with_impedance !== null && appointmentForm.data.with_impedance"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.impedance)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>

            <!-- With Impedance-->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{ $t('clinical.people.anamnesis.with_impedance') }}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="appointmentForm.data.with_impedance"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="appointmentForm.appointmentErrors.clear('with_impedance'); appointmentForm.appointmentErrors.clear('impedance')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('with_impedance')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('with_impedance')) }}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('impedance')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('impedance')) }}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>
        </ImpedanceForm>

        <TonalAudiometryForm
          ref="tonalAudiometryForm"
          class="ml-3"
          v-model="appointmentForm.data.tonal_audiometry"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('tonal_audiometry.')"
          @clearErrors="field => {appointmentForm.anamnesisErrors.clear(`tonal_audiometry.${field}`); appointmentForm.appointmentErrors.clear('tonal_audiometry')}"
          :editing="appointmentForm.data.with_tonal_audiometry !== null && appointmentForm.data.with_tonal_audiometry"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.tonal_audiometry)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>

            <!-- With Tonal Audiometry -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{ $t('clinical.people.anamnesis.with_tonal_audiometry') }}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="appointmentForm.data.with_tonal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="appointmentForm.appointmentErrors.clear('with_tonal_audiometry'); appointmentForm.appointmentErrors.clear('tonal_audiometry')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('with_tonal_audiometry')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('with_tonal_audiometry')) }}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('tonal_audiometry')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('tonal_audiometry')) }}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </TonalAudiometryForm>

        <VocalAudiometryForm
          ref="vocalAudiometryForm"
          class="ml-3"
          v-model="appointmentForm.data.vocal_audiometry"
          :formErrors="appointmentForm.anamnesisErrors.nestedErrors('vocal_audiometry.')"
          @clearErrors="field => {appointmentForm.anamnesisErrors.clear(`vocal_audiometry.${field}`); appointmentForm.appointmentErrors.clear('vocal_audiometry')}"
          :editing="appointmentForm.data.with_vocal_audiometry !== null && appointmentForm.data.with_vocal_audiometry"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge"
                      v-if="isOutdatedAnamnesisModel(appointmentForm.data.vocal_audiometry)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>

            <!-- With Vocal Audiometry -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{ $t('clinical.people.anamnesis.with_vocal_audiometry') }}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="appointmentForm.data.with_vocal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="appointmentForm.appointmentErrors.clear('with_vocal_audiometry'); appointmentForm.appointmentErrors.clear('vocal_audiometry')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('with_vocal_audiometry')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('with_vocal_audiometry')) }}
                </small>
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('vocal_audiometry')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('vocal_audiometry')) }}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </VocalAudiometryForm>

        <HearingDevicesForm
          ref="hearingDevicesForm"
          class="ml-3"
          v-model="appointmentForm.data.hearing_devices"
          :editing="appointmentForm.data.with_sale !== null && appointmentForm.data.with_sale"
          :collapsable="true"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <va-badge class="outdated-data-warning-badge"
                      v-if="areOutdatedHearingDevicesModels(appointmentForm.data.hearing_devices)">
              {{ $t('clinical.people.anamnesis.outdated_data_warning_message') }}
            </va-badge>

            <!-- With Hearing Device Sale -->
            <div class="row align-items-center mt-5">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{ $t('clinical.people.anamnesis.sale') }}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  v-model="appointmentForm.data.with_sale"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  @input="appointmentForm.appointmentErrors.clear('with_sale')"
                  class="col-4 col-lg-3 px-0"
                />
              </div>
              <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4"
                   v-if="appointmentForm.appointmentErrors.has('with_sale')">
                <small>
                  {{ $t(appointmentForm.appointmentErrors.get('with_sale')) }}
                </small>
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </HearingDevicesForm>

        <ObservationsForm
          class="ml-3"
          v-model="appointmentForm.data.anamnesis_obs"
          :formErrors="appointmentForm.appointmentErrors.rootErrors()"
          @input="field => appointmentForm.appointmentErrors.clear(field)"
          :collapsable="false"
        />

        <OutcomeForm
          class="ml-3"
          v-model="appointmentForm.data.outcome"
          :formErrors="appointmentForm.appointmentErrors.rootErrors()"
          :collapsable="false"
          :anamnese="sanitizedForm"
          :service="component.service"
          @input="field => appointmentForm.appointmentErrors.clear(field)"
          @clearErrors="field => appointmentForm.appointmentErrors.clear(field)"
        />
      </section>

      <div class="actions-wrapper d-flex flex-column flex-sm-row"
           :class="activeRequestHasUnsavedPersonChanges || activeRequestHasUnsavedAppointmentChanges ? 'justify-content-between' : 'justify-content-end'">
        <va-button
          v-show="activeRequestHasUnsavedAppointmentChanges || activeRequestHasUnsavedPersonChanges"
          class="btn-cancel order-2 order-sm-1"
          @click="endAppointment"
          :disabled="inCall || isSubmitingPersonForm || isSubmitingAppointmentForm || isEndingAppointment">
          {{ $t('evocall.requests.actions.end_without_saving') }}
        </va-button>

        <div class="d-flex justify-content-end align-items-center order-1 order-sm-2">
          <va-button
            id="btn-refresh"
            @click="onClickRefreshAppointmentData"
            v-if="needsRefreshToast">
            {{ $t('clinical.appointment.refresh_appointment_data_button') }}
          </va-button>

          <va-button
            id="btn-save"
            @click="submitForms"
            :disabled="!!(isSubmitingPersonForm || isSubmitingAppointmentForm || isEndingAppointment || needsRefreshToast || (!activeRequestHasUnsavedAppointmentChanges && !activeRequestHasUnsavedPersonChanges))">
            {{ $t('clinical.people.anamnesis.save') }}
          </va-button>

          <va-button
            id="btn-save-and-end"
            @click="saveAndEnd"
            :disabled="!!(inCall || isSubmitingPersonForm || isSubmitingAppointmentForm || isEndingAppointment || needsRefreshToast)">
            <!-- {{$t('evocall.requests.actions.save_and_end')}} -->
            {{
              activeRequestHasUnsavedAppointmentChanges || activeRequestHasUnsavedPersonChanges ? $t('clinical.appointments.end_appointment') : $t('clinical.appointments.end')
            }}
          </va-button>
        </div>

      </div>

    </div>

    <div v-else class="d-flex align-items-center flex-column py-5">
      <span class="mb-3">{{ $t('clinical.people.anamnesis.loading_failed') }}</span>
    </div>

    <ConfirmationModal
      v-if="showLeaveConfirmationModal"
      :ok-text="$t('clinical.people.anamnesis.common_options.yes')"
      :cancel-text="$t('clinical.people.anamnesis.common_options.no')"
      :title="$t('clinical.people.anamnesis.modal_has_unsaved_changes_title')"
      :body="$t('clinical.people.anamnesis.modal_has_unsaved_changes')"
      @modal-confirmed="onConfirmLeave"
      @modal-canceled="onCancelLeave"
    />

    <EndAppointmentModal
      v-if="showEndAppointmentModal"
      :appointment="appointment"
      :component="component"
      @modal-cancel="onModalEndAppointmentCancel"
      @modal-end-appointment-completed="onModalEndAppointmentCompleted"
      @modal-end-appointment-error="onModalEndAppointmentError"
    />
  </section>
</template>

<script>
import * as _ from 'lodash'
import {booleanOptions} from '@/components/clinical/people/Anamnesis/AnamnesisOptions'
import {HollowDotsSpinner} from 'epic-spinners'
import {mapState} from 'vuex'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import {AppointmentService, PersonService} from '@/api/AuthClinicalService'
import PersonForm from '@/components/clinical/people/PersonForm'
import ClinicalQuestionnaireForm from '@/components/clinical/people/Anamnesis/ClinicalQuestionnaireForm'
import LifeStyleForm from '@/components/clinical/people/Anamnesis/LifeStyleForm'
import OtoscopyForm from '@/components/clinical/people/Anamnesis/OtoscopyForm'
import TonalAudiometryForm from '@/components/clinical/people/Anamnesis/TonalAudiometryForm'
import VocalAudiometryForm from '@/components/clinical/people/Anamnesis/VocalAudiometryForm'
import HearingDevicesForm from '@/components/clinical/people/Anamnesis/HearingDevicesForm'
import ObservationsForm from '@/components/clinical/people/Anamnesis/ObservationsForm'
import OutcomeForm from '@/components/clinical/people/Anamnesis/OutcomeForm'
import AppointmentScheduleAndCompanion from '@/components/clinical/appointments/AppointmentScheduleAndCompanion'
import Errors from '@/utils/StandardErrors'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import EndAppointmentModal from '@/components/clinical/appointments/EndAppointmentModal'
import ImpedanceForm from "@/components/clinical/people/Anamnesis/ImpedanceForm";

export default {
  components: {
    ImpedanceForm,
    EndAppointmentModal,
    ConfirmationModal,
    OutcomeForm,
    Breadcrumbs,
    HollowDotsSpinner,
    PersonForm,
    ClinicalQuestionnaireForm,
    LifeStyleForm,
    OtoscopyForm,
    TonalAudiometryForm,
    VocalAudiometryForm,
    HearingDevicesForm,
    ObservationsForm,
    AppointmentScheduleAndCompanion
  },
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  mounted() {
    this.loadRequiredData(this.$route.params.id)
  },
  computed: {
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.component.service.slug}`
    },
    ...mapState({
      activeRequest(state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequest`] || null
      },
      inCall(state, getters) {
        return getters[`${this.waitingRoomModuleKey}/inCall`] || null
      },
      activeRequestHasUnsavedPersonChanges(state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequestHasUnsavedPersonChanges`] || false
      },
      activeRequestHasUnsavedAppointmentChanges(state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequestHasUnsavedAppointmentChanges`] || false
      }
    }),
    breadcrumbs: function () {
      return [
        {title: this.$t(`menu.${this.serviceSlug}`), disabled: true},
        {
          title: this.$t('menu.clinical/appointments'),
          meta: {type: 'string', value: `/${this.serviceSlug}/clinical/appointments`}
        },
        {
          title: this.$t('clinical.people.anamnesis.appointment_title', {name: this.appointment ? `${this.appointment.person.first_name} ${this.appointment.person.last_name}` : ''}),
          disabled: true,
          active: true
        },
      ]
    },
    sanitizedForm: function () {
      return {
        ...this.appointmentForm.data,
        hearing_devices: this.getHearingDevices(this.appointmentForm.data.hearing_devices),
        tonal_audiometry: this.getAnamnesisFormData(this.appointmentForm.data.tonal_audiometry)
      }
    }
  },
  data: function () {
    return {
      isLoading: true,
      hasLoadedAppointmentForm: false,
      hasLoadedPersonForm: false,
      hasUnsavedPersonChanges: false,
      hasUnsavedAppointmentChanges: false,
      onConfirmLeaveCallback: null,
      showLeaveConfirmationModal: false,
      showEndAppointmentModal: false,
      booleanOptions,
      isSubmitingPersonForm: false,
      isSubmitingAppointmentForm: false,
      isSubmitingAnamnesis: false,
      isEndingAppointment: false,
      needsRefreshToast: null,
      appointment: null,
      lastAppointment: null,
      person: null,
      personForm: {
        data: null,
        errors: new Errors()
      },
      appointmentForm: {
        data: {
          marketing_code: null,
          has_companion: false,
          has_companion_person: false,
          companion_person: null,
          companion: null,
          type_id: null,
          relationship_type_id: null,
          with_sale: false,
          with_otoscopy: false,
          with_impedance: false,
          with_tonal_audiometry: false,
          with_vocal_audiometry: false,
          anamnesis_obs: null,
          clinical_questionnaire: null,
          life_style: null,
          otoscopy: null,
          impedance: null,
          tonal_audiometry: null,
          vocal_audiometry: null,
          hearing_devices: null,
          outcome: null
        },
        anamnesisErrors: new Errors(),
        appointmentErrors: new Errors(),
        refreshToastObject: null
      }
    }
  },
  methods: {
    handleGoToCompanionFileClicked: function () {
      this.$router.push({path: `/${this.component.service.slug}/clinical/people/${this.appointmentForm.data.companion_person.id}`})
    },
    handleSaveAndEndClick: function () {
      this.showEndAppointmentModal = true
    },
    onModalEndAppointmentCompleted: function () {
      this.showToastInCall(this.$t('clinical.appointments.appointment_ended'), this.$store.state.successToastOptions)
      this.$router.push({name: `${this.component.service.slug}clinical/appointments`})
      this.showEndAppointmentModal = false
      this.isEndingAppointment = false
    },
    onModalEndAppointmentCancel: function () {
      this.isEndingAppointment = false
      this.showEndAppointmentModal = false
    },
    onModalEndAppointmentError: function () {
      setTimeout(() => {
        this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
      }, this.$store.state.errorToastOptions.duration)
    },
    onConfirmLeave: function () {
      this.showLeaveConfirmationModal = false
      this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, {hasUnsavedChanges: false})
      this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, {hasUnsavedChanges: false})
      if (this.onConfirmLeaveCallback !== null) {
        if (typeof this.onConfirmLeaveCallback === 'string') {
          this[this.onConfirmLeaveCallback]()
        } else {
          this.onConfirmLeaveCallback()
        }
      }
    },
    onCancelLeave: function () {
      this.showLeaveConfirmationModal = false
    },
    loadRequiredData: function (appointmentId) {
      this.isLoading = true
      this.loadAppointment(appointmentId)
        .then(appointment => {
          if (appointment.user.id === this.$auth.user().id && appointment.started_at !== null) {
            this.$store.commit(`${this.waitingRoomModuleKey}/ADD_ACTIVE_APPOINTMENT`, {appointment})
            this.loadPerson(appointment.person.id)
              .finally(() => {
                this.fillAppointmentForm()
                this.isLoading = false
              })
          } else {
            this.isLoading = false
            this.$router.go(-1)
          }
        })
        .catch(error => {
          console.log(error)
          this.isLoading = false
          switch (error.response.status) {
            case 403:
              this.showToast(this.$t('clinical.appointments.missing_permissions'), this.$store.state.errorToastOptions)
              this.$router.go(-1)
              break
            case 404:
              this.$router.go(-1)
              break
            default:
          }
        })
    },
    loadAppointment: function (appointmentId) {
      return new Promise((resolve, reject) => {
        AppointmentService.get(this.component.service.url, appointmentId)
          .then(appointment => {
            this.appointment = appointment
            this.$store.commit(`${this.waitingRoomModuleKey}/SET_CURRENT_ACTIVE_APPOINTMENT`, {appointment})
            resolve(appointment)
          })
          .catch(error => reject(error))
      })
    },
    loadPerson: function (personId) {
      return new Promise((resolve, reject) => {
        PersonService.get(this.component.service.url, personId)
          .then(person => {
            this.setPerson(person)
            resolve(person)
          })
          .catch(error => reject(error))
      })
    },
    setPerson: function (person) {
      this.hasLoadedPersonForm = false
      this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, {hasUnsavedChanges: false})
      this.$nextTick(() => {
        if (this.person == null) {
          this.person = person
        } else {
          this.person = {
            ...this.person,
            ...person
          }
        }
        // Person form works diferently so we cannot user this.$nextTick
        setTimeout(() => {
          this.hasLoadedPersonForm = true
        }, 500)
      })
    },
    endAppointment: function () {
      if (!this.isEndingAppointment) {
        if (this.inCall) {
          this.showToastInCall(this.$t('clinical.appointments.cannot_end_appointment_because_of_request'), this.$store.state.errorToastOptions)
        } else if (this.activeRequestHasUnsavedPersonChanges || this.activeRequestHasUnsavedAppointmentChanges) {
          this.showLeaveConfirmationModal = true
          this.onConfirmLeaveCallback = 'endAppointment'
        } else {
          this.isEndingAppointment = true
          this.showEndAppointmentModal = true
        }
      }
    },
    submitForms: function () {
      this.submitPersonForm()
      this.submitAppointmentForm()
    },
    submitPersonForm: function () {
      if (!this.isSubmitingPersonForm) {
        this.isSubmitingPersonForm = true
        // Update person through appointment: uses appointment->person_reference_id instead of person->reference_id
        AppointmentService.updateAppointmentPerson(this.component.service.url, this.appointment.id, this.getPersonFormData())
          .then(response => {
            let person = response.data.data
            this.showToastInCall(this.$t('clinical.appointments.person.update_success'), this.$store.state.successToastOptions)
            this.setPerson(person)
            this.$store.dispatch(`${this.waitingRoomModuleKey}/ACTIVE_REQUEST_CHANGES_SAVED`, {field: 'person'})
            if (response.data.error) {
              setTimeout(() => {
                this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
              }, this.$store.state.errorToastOptions.duration)
            }
          })
          .catch(errors => {
            console.log(errors)
            if (errors.response && errors.response.status) {
              switch (errors.response.status) {
                case 403:
                  this.showToastInCall(this.$t('clinical.appointments.person.missing_permissions'), this.$store.state.errorToastOptions)
                  break
                case 404:
                  this.showToastInCall(this.$t('clinical.people.errors.not_found'), this.$store.state.errorToastOptions)
                  break
                case 422:
                  this.showToastInCall(this.$t('clinical.appointments.person.form_contains_errors'), this.$store.state.errorToastOptions)
                  this.personForm.errors.record(errors.response.data.errors, 'people.errors.')
                  break
                default:
                  this.showToastInCall(this.$t('clinical.people.anamnesis.something_went_wrong'), this.$store.state.errorToastOptions)
                  break
              }
            }
          })
          .finally(() => {
            this.isSubmitingPersonForm = false
          })
      }
    },
    submitAppointmentForm: function () {
      if (!this.isSubmitingAppointmentForm) {
        this.isSubmitingAppointmentForm = true
        AppointmentService.update(this.component.service.url, this.appointment.id, this.getAppointmentFormData())
          .then(response => {
            let appointment = response.data.data
            this.showToastInCall(this.$t('clinical.appointments.update_with_anamnesis_success'), this.$store.state.successToastOptions)
            this.hasLoadedAppointmentForm = false
            this.appointment = appointment
            this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, {hasUnsavedChanges: false})
            this.$store.dispatch(`${this.waitingRoomModuleKey}/ACTIVE_REQUEST_CHANGES_SAVED`, {field: 'appointment'})
            this.fillAppointmentForm(true)
            if (response.data.error) {
              this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
            }
          })
          .catch(errors => {
            console.log(errors)
            if (errors.response && errors.response.status) {
              switch (errors.response.status) {
                case 403:
                  this.showToastInCall(this.$t('clinical.appointments.missing_permissions_for_update'), this.$store.state.errorToastOptions)
                  break
                case 404:
                  this.showToastInCall(this.$t('clinical.appointments.errors.not_found'), this.$store.state.errorToastOptions)
                  break
                case 422:
                  this.appointmentForm.appointmentErrors.record(errors.response.data.errors, 'clinical.appointments.errors.')
                  this.appointmentForm.anamnesisErrors.record(errors.response.data.errors, 'clinical.people.anamnesis.errors.')
                  this.showToastInCall(this.$t('clinical.appointments.appointment_form_contains_errors'), this.$store.state.errorToastOptions)
                  break
                default:
                  this.showToastInCall(this.$t('clinical.appointments.something_went_wrong'), this.$store.state.errorToastOptions)
                  break
              }
            }
          })
          .finally(() => {
            this.isSubmitingAppointmentForm = false
          })
      }
    },
    saveAndEnd: function () {
      if (!this.isSubmitingPersonForm) {
        this.isSubmitingPersonForm = true
        PersonService.update(this.component.service.url, this.person.id, this.getPersonFormData())
          .then(response => {
            if (response.data.error) {
              setTimeout(() => {
                this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
              }, this.$store.state.errorToastOptions.duration)
            }
            this.setPerson(response.data.data)
            this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, {hasUnsavedChanges: false})
            this.showToastInCall(this.$t('clinical.appointments.person.update_success'), this.$store.state.successToastOptions)

            if (!this.isSubmitingAppointmentForm) {
              this.isSubmitingAppointmentForm = true
              let data = this.getAppointmentFormData(true)

              AppointmentService.update(this.component.service.url, this.appointment.id, data)
                .then(response => {
                  if (response.data.error) {
                    setTimeout(() => {
                      this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
                    }, this.$store.state.successToastOptions.duration)
                  }
                  this.appointment = response.data.data
                  this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, {hasUnsavedChanges: false})
                  this.showToastInCall(this.$t('clinical.appointments.update_with_anamnesis_success'), this.$store.state.successToastOptions)
                  this.endAppointment()
                })
                .catch(errors => {
                  if (errors.response && errors.response.status) {
                    switch (errors.response.status) {
                      case 403:
                        this.showToastInCall(this.$t('clinical.appointments.missing_permissions_for_update'), this.$store.state.errorToastOptions)
                        break
                      case 404:
                        this.showToastInCall(this.$t('clinical.appointments.errors.not_found'), this.$store.state.errorToastOptions)
                        break
                      case 422:
                        this.appointmentForm.appointmentErrors.record(errors.response.data.errors, 'clinical.appointments.errors.')
                        this.appointmentForm.anamnesisErrors.record(errors.response.data.errors, 'clinical.people.anamnesis.errors.')
                        this.showToastInCall(this.$t('clinical.appointments.appointment_form_contains_errors'), this.$store.state.errorToastOptions)
                        break
                      default:
                        this.showToastInCall(this.$t('clinical.appointments.something_went_wrong'), this.$store.state.errorToastOptions)
                        break
                    }
                  }
                })
                .finally(() => {
                  this.isSubmitingAppointmentForm = false
                })
            }
          })
          .catch(errors => {
            if (errors.response && errors.response.status) {
              switch (errors.response.status) {
                case 403:
                  this.showToastInCall(this.$t('clinical.appointments.person.missing_permissions'), this.$store.state.errorToastOptions)
                  break
                case 404:
                  this.showToastInCall(this.$t('clinical.people.errors.not_found'), this.$store.state.errorToastOptions)
                  break
                case 422:
                  this.showToastInCall(this.$t('clinical.appointments.person.form_contains_errors'), this.$store.state.errorToastOptions)
                  this.personForm.errors.record(errors.response.data.errors, 'clinical.appointments.person.errors')
                  break
                default:
                  this.showToastInCall(this.$t('clinical.people.anamnesis.something_went_wrong'), this.$store.state.errorToastOptions)
                  break
              }
            }
          })
          .finally(() => {
            this.isSubmitingPersonForm = false
          })
      }
    },
    getPersonFormData: function () {
      let formData = JSON.parse(JSON.stringify(this.personForm.data))
      formData.entity_id = formData.entity ? formData.entity.id : null
      formData.gender = formData.gender ? formData.gender.id : null
      delete formData.entity
      return formData
    },
    getAppointmentFormData: function (isEnding = false) {
      let formData = JSON.parse(JSON.stringify(this.appointmentForm.data))

      formData.companion_person_id = formData.companion_person ? formData.companion_person.id : null
      delete formData.companion_person

      if (formData.status) {
        formData.status_id = formData.status.id
        delete formData.status
      }

      if (formData.status_reason) {
        formData.status_reason_id = formData.status_reason.id
        delete formData.status_reason
      }

      if (this.$refs.clinicalQuestionnaireForm.getHasUnsavedChanges()) {
        formData.clinical_questionnaire = this.getAnamnesisFormData(this.appointmentForm.data.clinical_questionnaire)
      } else {
        delete formData.clinical_questionnaire
      }

      if (this.$refs.lifeStyleForm.getHasUnsavedChanges()) {
        formData.life_style = this.getAnamnesisFormData(this.appointmentForm.data.life_style)
      } else {
        delete formData.life_style
      }

      if (this.$refs.otoscopyForm.getHasUnsavedChanges()) {
        if (this.appointmentForm.data.with_otoscopy) {
          formData.otoscopy = this.getAnamnesisFormData(this.appointmentForm.data.otoscopy)
        } else {
          delete formData.otoscopy
        }
      } else {
        delete formData.with_otoscopy
        delete formData.otoscopy
      }

      if (this.$refs.impedanceForm.getHasUnsavedChanges()) {
        if (this.appointmentForm.data.with_impedance) {
          formData.impedance = this.getAnamnesisFormData(this.appointmentForm.data.impedance)
        } else {
          delete formData.impedance
        }
      } else {
        delete formData.with_impedance
        delete formData.impedance
      }

      if (this.$refs.tonalAudiometryForm.getHasUnsavedChanges()) {
        if (this.appointmentForm.data.with_tonal_audiometry) {
          formData.tonal_audiometry = this.getAnamnesisFormData(this.appointmentForm.data.tonal_audiometry)
        } else {
          delete formData.tonal_audiometry
        }
      } else {
        delete formData.with_tonal_audiometry
        delete formData.tonal_audiometry
      }

      if (this.$refs.vocalAudiometryForm.getHasUnsavedChanges()) {
        if (this.appointmentForm.data.with_vocal_audiometry) {
          formData.vocal_audiometry = this.getAnamnesisFormData(this.appointmentForm.data.vocal_audiometry)
        } else {
          delete formData.vocal_audiometry
        }
      } else {
        delete formData.with_vocal_audiometry
        delete formData.vocal_audiometry
      }

      if (this.$refs.hearingDevicesForm.getHasUnsavedChanges()) {
        if (this.appointmentForm.data.with_sale) {
          formData.hearing_devices = this.getHearingDevices(this.appointmentForm.data.hearing_devices)
        } else {
          delete formData.hearing_devices
        }
      } else {
        delete formData.with_sale
        delete formData.hearing_devices
      }

      formData.is_ending = isEnding

      if (isEnding || this.appointmentForm.data.outcome.status_id !== null || (this.appointment.status && this.appointment.status.id !== null)) {
        formData = {...formData, ...formData.outcome}
        delete formData.outcome
      }
      return formData
    },
    getAnamnesisFormData: function (form) {
      if (form !== null) {
        let formData = this.sanitizeAnamnesisModel(form)
        let id = form.hasOwnProperty('id') ? form.id : undefined
        delete formData.id

        formData = Object.values(formData).some(p => (Array.isArray(p) && p.length > 0) || (!Array.isArray(p) && p !== null && p !== undefined && (typeof p === 'string' ? p.trim() !== '' : true))) ? formData : undefined

        if (formData !== undefined && id !== undefined && id !== null) {
          formData.id = id
        }

        return formData
      } else {
        return undefined
      }
    },
    getHearingDevices: function (hearingDevices) {
      let values = []
      if (hearingDevices) {
        values = hearingDevices.map(hearingDevice => {
          return this.sanitizeAnamnesisModel(hearingDevice)
        })
          .filter(hearingDevice => {
            return Object.values(hearingDevice)
              .filter(p => p !== 'left' && p !== 'right')
              .some(p => (Array.isArray(p) && p.length > 0) || (!Array.isArray(p) && p !== null && p !== undefined && (typeof p === 'string' ? p.trim() !== '' : true)))
          })
      }
      return values.length > 0 ? values : undefined
    },
    sanitizeAnamnesisModel: function (model) {
      let data = JSON.parse(JSON.stringify(model))
      delete data.appointment_id
      delete data.person_id
      delete data.created_by
      delete data.created_at
      delete data.updated_by
      delete data.updated_at
      return data
    },
    getLatestAnamnesisModel: function (models, defaultValue = null) {
      if (Array.isArray(models) && models.length > 0) {
        models.sort((a, b) => this.moment(b.updated_at).diff(a.updated_at))
        return models[0]
      }
      return defaultValue
    },
    getLatestAppointmentHearingDevices: function (defaultValue = null) {
      const appointments = [this.appointment, this.appointment.call_request_appointments].flat()

      let models = appointments.map(appointment => _.get(appointment, 'hearing_devices', null))
        .filter(v => Array.isArray(v) && v.length > 0)
        .flat()

      return this.getLatestHearingDevices(models, defaultValue)
    },
    getLatestPersonHearingDevices: function (defaultValue = null) {
      const models = this.person.hearing_devices.filter(model => model.appointment_id !== this.appointment.id)
      return this.getLatestHearingDevices(models, defaultValue)
    },
    getLatestHearingDevices: function (models, defaultValue = null) {
      if (Array.isArray(models) && models.length > 0) {
        models = models.filter(v => v !== null)

        if (models.length > 0) {
          models.sort((a, b) => this.moment(b.updated_at).diff(a.updated_at))

          let leftHearingDevice = models.find(hearingDevice => hearingDevice.ear === 'left')
          let rightHearingDevice = models.find(hearingDevice => hearingDevice.ear === 'right')

          let hearingDevices = [rightHearingDevice, leftHearingDevice].filter(v => v)
          return hearingDevices.length > 0 ? hearingDevices : defaultValue
        }
      }
      return defaultValue
    },
    showToastInCall: function (message, options) {
      let customOptions = {...options}
      if (this.inCall) {
        customOptions.position = 'top-left'
      }
      return this.showToast(message, customOptions)
    },
    showNeedsRefreshToast: function () {
      let customOptions = {
        ...this.$store.state.successToastOptions,
        duration: null,
        position: 'top-left',
        action: {
          text: this.$t('clinical.appointment.refresh_appointment_data_button'),
          onClick: (e, toastObject) => {
            this.onClickRefreshAppointmentData()
          }
        }
      }
      this.needsRefreshToast = this.showToastInCall(this.$t('clinical.appointment.needs_refresh'), {...customOptions})
    },
    onClickRefreshAppointmentData: function () {
      this.needsRefreshToast.goAway(250)
      this.hasLoadedAppointmentForm = false
      this.needsRefreshToast = null
      Promise.all([this.loadPerson(this.appointment.person.id), this.loadAppointment(this.appointment.id)])
        .then(() => {
          this.fillAppointmentFormFromRefresh()
          this.$nextTick(() => {
            if (this.getUnsavedSectionsCount() === 0) {
              this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, {hasUnsavedChanges: false})
            }
          })
        })
    },
    fillAppointmentCommonFields: function () {
      this.appointmentForm.data.marketing_code = this.appointment.marketing_code
      this.appointmentForm.data.has_companion = this.appointment.has_companion
      this.appointmentForm.data.companion = this.appointment.companion
      this.appointmentForm.data.has_companion_person = this.appointment.has_companion_person
      this.appointmentForm.data.companion_person = this.appointment.companion_person
      this.appointmentForm.data.relationship_type_id = this.appointment.relationship_type_id
      this.appointmentForm.data.type_id = this.appointment.type_id
      this.appointmentForm.data.with_sale = this.appointment.with_sale || false
      this.appointmentForm.data.with_otoscopy = this.appointment.with_otoscopy || false
      this.appointmentForm.data.with_impedance = this.appointment.with_impedance || false
      this.appointmentForm.data.with_tonal_audiometry = this.appointment.with_tonal_audiometry || false
      this.appointmentForm.data.with_vocal_audiometry = this.appointment.with_vocal_audiometry || false
      this.appointmentForm.data.anamnesis_obs = this.appointment.anamnesis_obs
      this.appointmentForm.data.outcome = {
        status_id: this.appointment.status ? this.appointment.status.id : null,
        status_reason_id: this.appointment.status_reason ? this.appointment.status_reason.id : null
      }
    },
    fillAppointmentForm: function (resetUnsavedChanges = false) {
      this.fillAppointmentCommonFields()

      this.setAppointmentFormField('clinical_questionnaires', 'clinical_questionnaire')
      this.setAppointmentFormField('life_styles', 'life_style')
      this.setAppointmentFormField('otoscopies', 'otoscopy')
      this.setAppointmentFormField('impedances', 'impedance')
      this.setAppointmentFormField('tonal_audiometries', 'tonal_audiometry')
      this.setAppointmentFormField('vocal_audiometries', 'vocal_audiometry')
      this.setHearingDevicesAppointmentFormField()

      if (resetUnsavedChanges) {
        this.resetUnsavedChanges()
      }

      this.$nextTick(() => {
        this.hasLoadedAppointmentForm = true
      })
    },
    fillAppointmentFormFromRefresh: function () {
      // Update if audiologist says "Yes it was performed, and has changes"
      // If audiologist backs out and says "No, it was not performed", maintain the current unsaved changes
      let callRequests = [...this.appointment.call_request_appointments].filter(appointment => appointment.started_at !== null)

      if (callRequests.length > 0) {
        callRequests.sort((a, b) => {
          return this.moment(b.started_at).diff(this.moment(a.started_at))
        })

        let callRequestAppointment = callRequests[0]
        console.log(callRequests.map(c => c.started_at))
        console.log('here ----- ')
        console.log(callRequestAppointment)

        if (!(this.$refs.otoscopyForm.getHasUnsavedChanges() && !callRequestAppointment.with_otoscopy)) {
          this.setAppointmentFormField('otoscopies', 'otoscopy')
          this.$nextTick(() => {
            this.$refs.otoscopyForm.setHasUnsavedChanges(false)
          })
        }

        if (!(this.$refs.impedanceForm.getHasUnsavedChanges() && !callRequestAppointment.with_impedance)) {
          this.setAppointmentFormField('impedances', 'impedance')
          this.$nextTick(() => {
            this.$refs.impedanceForm.setHasUnsavedChanges(false)
          })
        }

        if (!(this.$refs.tonalAudiometryForm.getHasUnsavedChanges() && !callRequestAppointment.with_tonal_audiometry)) {
          this.setAppointmentFormField('tonal_audiometries', 'tonal_audiometry')
          this.$nextTick(() => {
            this.$refs.tonalAudiometryForm.setHasUnsavedChanges(false)
          })
        }

        if (!(this.$refs.vocalAudiometryForm.getHasUnsavedChanges() && !callRequestAppointment.with_vocal_audiometry)) {
          this.setAppointmentFormField('vocal_audiometries', 'vocal_audiometry')
          this.$nextTick(() => {
            this.$refs.vocalAudiometryForm.setHasUnsavedChanges(false)
          })
        }

        this.$nextTick(() => {
          this.hasLoadedAppointmentForm = true
        })
      }
    },
    setAppointmentFormField: function (modelField, formField, useAppointmentData = true) {
      let models = []
      const appointments = [this.appointment, this.appointment.call_request_appointments].flat()

      if (useAppointmentData) {
        models = models.concat(
          appointments.map(appointment => _.get(appointment, modelField, null))
            .filter(v => Array.isArray(v) && v.length > 0)
            .flat()
        )
      }

      if (Array.isArray(this.person[modelField]) && this.person[modelField].length > 0) {
        models = models.concat(
          this.person[modelField].filter(model => !appointments.some(appointment => appointment.id === model.appointment_id))
        )
      }

      let latestModel = this.getLatestAnamnesisModel(models, null)

      if (latestModel !== null) {
        latestModel = JSON.parse(JSON.stringify(latestModel))

        if (latestModel.appointment_id !== this.appointment.id) {
          delete latestModel.id
        }
      }

      this.appointmentForm.data[formField] = latestModel
    },
    setHearingDevicesAppointmentFormField: function (useAppointmentData = true) {
      let latestModels = null

      if (useAppointmentData) {
        latestModels = this.getLatestAppointmentHearingDevices()
      }

      if (latestModels === null) {
        latestModels = this.getLatestPersonHearingDevices()

        if (latestModels !== null) {
          latestModels = latestModels.map(model => {
            delete model.id
            return model
          })
        }
      }

      this.appointmentForm.data.hearing_devices = latestModels
    },
    getUnsavedSectionsCount: function () {
      return this.getAnamnesisFormsFromRefs().filter(form => form.getHasUnsavedChanges()).length
    },
    resetUnsavedChanges: function () {
      this.getAnamnesisFormsFromRefs()
        .forEach(form => {
          form.setHasUnsavedChanges(false)
        })
    },
    getAnamnesisFormsFromRefs: function () {
      return Object.keys(this.$refs).filter(key => key.toLowerCase().includes('form')).map(key => this.$refs[key])
    },
    isOutdatedAnamnesisModel: function (model) {
      return model !== null && this.person.last_appointment_date !== null && this.moment(model.updated_at).isBefore(this.moment(this.person.last_appointment_date))
    },
    areOutdatedHearingDevicesModels: function (hearingDevices) {
      if (Array.isArray(hearingDevices) && this.person.last_appointment_date !== null) {
        return hearingDevices.every(hearingDevice => this.moment(hearingDevice.updated_at).isBefore(this.moment(this.person.last_appointment_date)))
      } else {
        return false
      }
    }
  },
  sockets: {
    'active-request-appointment-updated': function () {
      if (this.needsRefreshToast === null) {
        this.showNeedsRefreshToast()
      }
    }
  },
  watch: {
    'appointmentForm.data.with_otoscopy': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('otoscopies', 'otoscopy', false)
      }

      if (this.$refs.hasOwnProperty('otoscopyForm') && newVal !== oldVal) {
        this.$refs.otoscopyForm.setHasUnsavedChanges(true)
      }
    },
    'appointmentForm.data.with_impedance': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('impedances', 'impedance', false)
      }

      if (this.$refs.hasOwnProperty('impedanceForm') && newVal !== oldVal) {
        this.$refs.impedanceForm.setHasUnsavedChanges(true)
      }
    },
    'appointmentForm.data.with_tonal_audiometry': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('tonal_audiometries', 'tonal_audiometry', false)
      }

      if (this.$refs.hasOwnProperty('tonalAudiometryForm') && newVal !== oldVal) {
        this.$refs.tonalAudiometryForm.setHasUnsavedChanges(true)
      }
    },
    'appointmentForm.data.with_vocal_audiometry': function (newVal, oldVal) {
      if (!newVal) {
        this.setAppointmentFormField('vocal_audiometries', 'vocal_audiometry', false)
      }

      if (this.$refs.hasOwnProperty('vocalAudiometryForm') && newVal !== oldVal) {
        this.$refs.vocalAudiometryForm.setHasUnsavedChanges(true)
      }
    },
    'appointmentForm.data.with_sale': function (newVal, oldVal) {
      if (!newVal) {
        this.setHearingDevicesAppointmentFormField(false)
      }

      if (this.$refs.hasOwnProperty('hearingDevicesForm') && newVal !== oldVal) {
        this.$refs.hearingDevicesForm.setHasUnsavedChanges(true)
      }
    },
    'personForm.data': {
      handler: function (newVal, oldVal) {
        if (this.hasLoadedPersonForm && !this.activeRequestHasUnsavedPersonChanges) {
          this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_PERSON_CHANGES`, {hasUnsavedChanges: true})
        }
      },
      deep: true
    },
    'appointmentForm.data': {
      handler: function (value) {
        if (this.hasLoadedAppointmentForm && !this.activeRequestHasUnsavedAppointmentChanges) {
          this.$store.commit(`${this.waitingRoomModuleKey}/SET_ACTIVE_APPOINTMENT_UNSAVED_APPOINTMENT_CHANGES`, {hasUnsavedChanges: true})
        }
      },
      deep: true
    }
  },
  beforeDestroy: function () {
    if (this.needsRefreshToast != null) {
      this.needsRefreshToast.goAway(100)
    }
  },
  beforeRouteEnter(to, from, next) {
    if (to.params.id !== undefined) {
      next()
    } else {
      next(from)
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.params.id !== undefined) {
      next()
    } else {
      next(from)
    }
  },
  beforeRouteLeave: function (to, from, next) {
    if (this.activeRequestHasUnsavedAppointmentChanges || this.activeRequestHasUnsavedPersonChanges) {
      this.showLeaveConfirmationModal = true
      this.onConfirmLeaveCallback = next
    } else {
      this.$store.commit(`${this.waitingRoomModuleKey}/RESET_CURRENT_ACTIVE_APPOINTMENT`)
      next()
    }
  },
}
</script>

<style lang="scss">

#appointment-questionnaire {

  .questionnaire-section {
    margin-bottom: 3rem;
  }

  .actions-wrapper {

    .va-button {
      min-width: 8rem;
    }
  }
}
</style>
